import isEqual from 'lodash/isEqual';
import type { Commonv1Value } from '@/generated/hooks/grimoire/models';
import { createStore } from './config';

/**
 * Data structure for storing automation input values and validation state
 */
interface AutomationInputsData {
  /** Map of input field names to their Commonv1Value values */
  values: Record<string, Commonv1Value>;
  /** Optional map of input field names to validation error messages */
  errors?: Record<string, string>;
  /** Input names that are marked as default */
  defaultInputKeys: string[];
  /** Original default input keys from the API (for change detection) */
  originalDefaultInputKeys: string[];
  /** Original default input values from the API (for change detection) */
  originalDefaultInputValues: Record<string, Commonv1Value>;
}

/**
 * Zustand store state for managing automation input values across automations
 *
 * This store persists input data in sessionStorage, allowing users to maintain
 * input values during their session even when navigating between pages.
 */
interface AutomationInputsState {
  // State: Map of automationId to input data
  /** Map of automation IDs to their input data */
  automationInputs: Record<string, AutomationInputsData>;

  // Actions
  /**
   * Sets or updates the input values and errors for a specific automation
   *
   * @param automationId - The unique identifier of the automation
   * @param values - Map of input field names to their values
   * @param errors - Optional map of input field names to error messages
   */
  setAutomationInputs: (
    automationId: string,
    values: Record<string, Commonv1Value>,
    errors?: Record<string, string>,
  ) => void;

  /**
   * Retrieves the input data for a specific automation
   *
   * @param automationId - The unique identifier of the automation
   * @returns The automation input data, or undefined if not found
   */
  getAutomationInputs: (
    automationId: string,
  ) => AutomationInputsData | undefined;

  /**
   * Removes all input data for a specific automation
   *
   * @param automationId - The unique identifier of the automation to clear
   */
  clearAutomationInputs: (automationId: string) => void;

  /**
   * Clears all values and default input keys for an automation while preserving
   * original default tracking. This allows Save to detect the change and persist
   * empty default_inputs to the API.
   *
   * @param automationId - The automation to clear
   */
  clearValuesAndDefaults: (automationId: string) => void;

  /**
   * Clears all input data for all automations
   */
  clearAllInputs: () => void;

  /**
   * Clears non-default input values and errors for all automations.
   * Preserves default input keys, their values, and original default tracking fields.
   * For each automation entry, only the values of default inputs are kept;
   * all other values and all errors are cleared.
   */
  clearNonDefaultInputs: () => void;

  /**
   * Toggles an input as default or non-default for a specific automation
   *
   * @param automationId - The unique identifier of the automation
   * @param inputName - The name of the input to toggle
   */
  toggleDefaultInput: (automationId: string, inputName: string) => void;

  /**
   * Initializes default input keys and values from the API response.
   * Populates input values from defaults only when the store has no existing values.
   *
   * @param automationId - The unique identifier of the automation
   * @param defaultInputs - The default_inputs map from the getAutomation API response
   */
  initializeDefaultInputs: (
    automationId: string,
    defaultInputs: Record<string, Commonv1Value>,
  ) => void;

  /**
   * Checks whether the default inputs have changed compared to what was loaded from the API
   *
   * @param automationId - The unique identifier of the automation
   * @returns Whether default input keys or their values have changed
   */
  hasDefaultInputsChanged: (automationId: string) => boolean;

  /**
   * Updates the original default inputs to match the current state (after a successful API save)
   *
   * @param automationId - The unique identifier of the automation
   */
  syncOriginalDefaults: (automationId: string) => void;
}

export const useAutomationInputsStore = createStore<AutomationInputsState>(
  'automation-inputs-store',
  (_, get, api) => ({
    // Initial state
    automationInputs: {},

    // Actions
    setAutomationInputs: (
      automationId: string,
      values: Record<string, Commonv1Value>,
      errors?: Record<string, string>,
    ) => {
      const currentInputs = get().automationInputs;
      const existing = currentInputs[automationId];
      api.setState(
        {
          automationInputs: {
            ...currentInputs,
            [automationId]: {
              ...existing,
              values,
              errors,
              defaultInputKeys: existing?.defaultInputKeys ?? [],
              originalDefaultInputKeys:
                existing?.originalDefaultInputKeys ?? [],
              originalDefaultInputValues:
                existing?.originalDefaultInputValues ?? {},
            },
          },
        },
        false,
        {
          type: 'automationInputs/setAutomationInputs',
          automationId,
          valuesCount: Object.keys(values).length,
        },
      );
    },

    getAutomationInputs: (
      automationId: string,
    ): AutomationInputsData | undefined => {
      return get().automationInputs[automationId];
    },

    clearAutomationInputs: (automationId: string) => {
      const currentInputs = { ...get().automationInputs };
      delete currentInputs[automationId];
      api.setState({ automationInputs: currentInputs }, false, {
        type: 'automationInputs/clearAutomationInputs',
        automationId,
      });
    },

    clearValuesAndDefaults: (automationId: string) => {
      const currentInputs = get().automationInputs;
      const existing = currentInputs[automationId];
      if (!existing) return;

      api.setState(
        {
          automationInputs: {
            ...currentInputs,
            [automationId]: {
              ...existing,
              values: {},
              errors: undefined,
              defaultInputKeys: [],
            },
          },
        },
        false,
        {
          type: 'automationInputs/clearValuesAndDefaults',
          automationId,
        },
      );
    },

    clearAllInputs: () => {
      api.setState(
        { automationInputs: {} },
        false,
        'automationInputs/clearAllInputs',
      );
    },

    clearNonDefaultInputs: () => {
      const currentInputs = get().automationInputs;
      const newInputs: Record<string, AutomationInputsData> = {};

      for (const [automationId, data] of Object.entries(currentInputs)) {
        const defaultKeys = data.defaultInputKeys;

        // If no defaults, skip this automation entirely (fully cleared)
        if (!defaultKeys || defaultKeys.length === 0) {
          continue;
        }

        // Keep only default input values
        const preservedValues: Record<string, Commonv1Value> = {};
        for (const key of defaultKeys) {
          if (data.values[key]) {
            preservedValues[key] = data.values[key];
          }
        }

        newInputs[automationId] = {
          values: preservedValues,
          errors: undefined,
          defaultInputKeys: data.defaultInputKeys,
          originalDefaultInputKeys: data.originalDefaultInputKeys,
          originalDefaultInputValues: data.originalDefaultInputValues,
        };
      }

      api.setState(
        { automationInputs: newInputs },
        false,
        'automationInputs/clearNonDefaultInputs',
      );
    },

    toggleDefaultInput: (automationId: string, inputName: string) => {
      const currentInputs = get().automationInputs;
      const existing = currentInputs[automationId];
      if (!existing) return;

      const currentKeys = existing.defaultInputKeys;
      const isDefault = currentKeys.includes(inputName);
      const newKeys = isDefault
        ? currentKeys.filter(k => k !== inputName)
        : [...currentKeys, inputName];

      api.setState(
        {
          automationInputs: {
            ...currentInputs,
            [automationId]: {
              ...existing,
              defaultInputKeys: newKeys,
            },
          },
        },
        false,
        {
          type: 'automationInputs/toggleDefaultInput',
          automationId,
          inputName,
          isDefault: !isDefault,
        },
      );
    },

    initializeDefaultInputs: (
      automationId: string,
      defaultInputs: Record<string, Commonv1Value>,
    ) => {
      const currentInputs = get().automationInputs;
      const existing = currentInputs[automationId];
      const defaultKeys = Object.keys(defaultInputs);

      // Always use defaults as the base, letting existing user-entered values
      // take precedence. This ensures newly added defaults are included even
      // when the store already has partial values from a previous mount.
      const mergedValues = { ...existing?.values, ...defaultInputs };

      api.setState(
        {
          automationInputs: {
            ...currentInputs,
            [automationId]: {
              values: mergedValues,
              errors: existing?.errors,
              defaultInputKeys: defaultKeys,
              originalDefaultInputKeys: [...defaultKeys],
              originalDefaultInputValues: { ...defaultInputs },
            },
          },
        },
        false,
        {
          type: 'automationInputs/initializeDefaultInputs',
          automationId,
          defaultKeysCount: defaultKeys.length,
        },
      );
    },

    hasDefaultInputsChanged: (automationId: string): boolean => {
      const existing = get().automationInputs[automationId];
      if (!existing) return false;

      const currentKeys = [...existing.defaultInputKeys].sort();
      const originalKeys = [...existing.originalDefaultInputKeys].sort();

      // Check if keys changed
      if (currentKeys.length !== originalKeys.length) return true;
      if (currentKeys.some((key, i) => key !== originalKeys[i])) return true;

      // Check if values of default inputs changed
      const currentValues = existing.values;
      const originalValues = existing.originalDefaultInputValues;
      for (const key of currentKeys) {
        if (!isEqual(currentValues[key], originalValues[key])) {
          return true;
        }
      }

      return false;
    },

    syncOriginalDefaults: (automationId: string) => {
      const currentInputs = get().automationInputs;
      const existing = currentInputs[automationId];
      if (!existing) return;

      const newOriginalValues: Record<string, Commonv1Value> = {};
      for (const key of existing.defaultInputKeys) {
        if (existing.values[key]) {
          newOriginalValues[key] = existing.values[key];
        }
      }

      api.setState(
        {
          automationInputs: {
            ...currentInputs,
            [automationId]: {
              ...existing,
              originalDefaultInputKeys: [...existing.defaultInputKeys],
              originalDefaultInputValues: newOriginalValues,
            },
          },
        },
        false,
        {
          type: 'automationInputs/syncOriginalDefaults',
          automationId,
        },
      );
    },
  }),
);

// Selectors
/**
 * Selector to get all automation inputs from the store
 *
 * @param state - The automation inputs store state
 * @returns Map of automation IDs to their input data
 */
export const selectAutomationInputs = (state: AutomationInputsState) =>
  state.automationInputs;

/**
 * Selector factory to get the input data for a specific automation.
 * Returns a stable `undefined` when the automation has no entry, avoiding
 * unnecessary re-renders from other automations' changes.
 *
 * @param automationId - The automation to select inputs for
 * @returns Selector that returns the AutomationInputsData or undefined
 */
export const selectAutomationInputsForId =
  (automationId: string | undefined) => (state: AutomationInputsState) =>
    automationId ? state.automationInputs[automationId] : undefined;

/**
 * Selector to get the setAutomationInputs action
 *
 * @param state - The automation inputs store state
 * @returns The setAutomationInputs action function
 */
export const selectSetAutomationInputs = (state: AutomationInputsState) =>
  state.setAutomationInputs;

/**
 * Selector to get the getAutomationInputs action
 *
 * @param state - The automation inputs store state
 * @returns The getAutomationInputs action function
 */
export const selectGetAutomationInputs = (state: AutomationInputsState) =>
  state.getAutomationInputs;

/**
 * Selector to get the clearAutomationInputs action
 *
 * @param state - The automation inputs store state
 * @returns The clearAutomationInputs action function
 */
export const selectClearAutomationInputs = (state: AutomationInputsState) =>
  state.clearAutomationInputs;

/**
 * Selector to get the clearValuesAndDefaults action
 *
 * @param state - The automation inputs store state
 * @returns The clearValuesAndDefaults action function
 */
export const selectClearValuesAndDefaults = (state: AutomationInputsState) =>
  state.clearValuesAndDefaults;

/**
 * Selector to get the clearAllInputs action
 *
 * @param state - The automation inputs store state
 * @returns The clearAllInputs action function
 */
export const selectClearAllInputs = (state: AutomationInputsState) =>
  state.clearAllInputs;

/**
 * Selector to get the clearNonDefaultInputs action
 *
 * @param state - The automation inputs store state
 * @returns The clearNonDefaultInputs action function
 */
export const selectClearNonDefaultInputs = (state: AutomationInputsState) =>
  state.clearNonDefaultInputs;

/**
 * Selector to get the toggleDefaultInput action
 *
 * @param state - The automation inputs store state
 * @returns The toggleDefaultInput action function
 */
export const selectToggleDefaultInput = (state: AutomationInputsState) =>
  state.toggleDefaultInput;

/**
 * Selector to get the initializeDefaultInputs action
 *
 * @param state - The automation inputs store state
 * @returns The initializeDefaultInputs action function
 */
export const selectInitializeDefaultInputs = (state: AutomationInputsState) =>
  state.initializeDefaultInputs;

/**
 * Selector to get the hasDefaultInputsChanged action
 *
 * @param state - The automation inputs store state
 * @returns The hasDefaultInputsChanged action function
 */
export const selectHasDefaultInputsChanged = (state: AutomationInputsState) =>
  state.hasDefaultInputsChanged;

/**
 * Selector to get the syncOriginalDefaults action
 *
 * @param state - The automation inputs store state
 * @returns The syncOriginalDefaults action function
 */
export const selectSyncOriginalDefaults = (state: AutomationInputsState) =>
  state.syncOriginalDefaults;
